import { useEffect, useRef, useState } from "react";
import { X, Camera, Loader2, ScanLine, Check } from "lucide-react";

const PROC_W = 480; // résolution d'analyse (downscale)

export default function DocumentScanner({ onCapture, onClose }) {
  const videoRef = useRef(null);
  const overlayRef = useRef(null);
  const procRef = useRef(null);
  const rafRef = useRef(null);
  const workerRef = useRef(null);
  const busyRef = useRef(false);
  const lastSentRef = useRef(0);
  const lastWHRef = useRef({ W: PROC_W, H: PROC_W });
  const lastQuad = useRef(null);
  const stableCount = useRef(0);
  const capturedRef = useRef(false);
  const cvReadyRef = useRef(false);
  const autoRef = useRef(true);
  const hintRef = useRef("");
  const [status, setStatus] = useState("loading"); // loading | ready | error | captured
  const [auto, setAutoState] = useState(true);
  const [cvReady, setCvReady] = useState(false);
  const [hint, setHint] = useState("Démarrage de la caméra…");

  const setAuto = (v) => {
    const nv = typeof v === "function" ? v(autoRef.current) : v;
    autoRef.current = nv;
    setAutoState(nv);
  };
  const showHint = (t) => { if (hintRef.current !== t) { hintRef.current = t; setHint(t); } };

  useEffect(() => {
    let stream;

    const stopCamera = () => {
      cancelAnimationFrame(rafRef.current);
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
    const stop = () => {
      stopCamera();
      if (workerRef.current) { workerRef.current.terminate(); workerRef.current = null; }
    };

    const grabProc = (video) => {
      const vw = video.videoWidth, vh = video.videoHeight;
      const W = PROC_W, H = Math.max(1, Math.round((vh / vw) * PROC_W));
      const proc = procRef.current;
      proc.width = W; proc.height = H;
      const ctx = proc.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(video, 0, 0, W, H);
      return { img: ctx.getImageData(0, 0, W, H), W, H };
    };

    const grabFull = (video) => {
      const vw = video.videoWidth, vh = video.videoHeight;
      const c = document.createElement("canvas");
      c.width = vw; c.height = vh;
      const ctx = c.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(video, 0, 0, vw, vh);
      return { img: ctx.getImageData(0, 0, vw, vh), vw, vh, canvas: c };
    };

    const drawOverlay = (video, quad) => {
      const ov = overlayRef.current;
      if (!ov) return;
      const rect = video.getBoundingClientRect();
      ov.width = rect.width; ov.height = rect.height;
      const ctx = ov.getContext("2d");
      ctx.clearRect(0, 0, ov.width, ov.height);
      if (!quad) return;
      const rx = rect.width / quad.W, ry = rect.height / quad.H;
      ctx.beginPath();
      quad.pts.forEach((p, idx) => {
        const x = p.x * rx, y = p.y * ry;
        idx === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      });
      ctx.closePath();
      ctx.fillStyle = "rgba(224,90,71,0.18)";
      ctx.fill();
      ctx.lineWidth = 3; ctx.strokeStyle = "#E05A47"; ctx.stroke();
      quad.pts.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x * rx, p.y * ry, 6, 0, Math.PI * 2);
        ctx.fillStyle = "#fff"; ctx.fill();
        ctx.lineWidth = 2; ctx.strokeStyle = "#E05A47"; ctx.stroke();
      });
    };

    const similar = (a, b) => {
      if (!a || !b) return false;
      let d = 0;
      for (let i = 0; i < 4; i++) d += Math.hypot(a.pts[i].x - b.pts[i].x, a.pts[i].y - b.pts[i].y);
      return d < 40;
    };

    const captureFullFrame = () => {
      const video = videoRef.current;
      const { canvas } = grabFull(video);
      capturedRef.current = true;
      setStatus("captured");
      canvas.toBlob((b) => b && onCapture(b), "image/jpeg", 0.92);
      stop();
    };

    const captureWarp = (quad) => {
      const video = videoRef.current;
      const w = workerRef.current;
      if (!w) { captureFullFrame(); return; }
      const { img, vw, vh } = grabFull(video);
      capturedRef.current = true;
      setStatus("captured");
      stopCamera(); // fige l'aperçu ; le worker reste vivant pour redresser l'image
      w.postMessage({ cmd: "warp", imageData: img, vw, vh, quad }, [img.data.buffer]);
    };

    // boucle légère : envoie une frame au worker seulement s'il est libre
    const tick = () => {
      const video = videoRef.current;
      if (video && video.readyState === 4 && !capturedRef.current && workerRef.current && cvReadyRef.current && !busyRef.current) {
        const now = performance.now();
        if (now - lastSentRef.current >= 240) {
          lastSentRef.current = now;
          const { img, W, H } = grabProc(video);
          lastWHRef.current = { W, H };
          busyRef.current = true;
          workerRef.current.postMessage({ cmd: "detect", imageData: img, W, H }, [img.data.buffer]);
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    const onWorkerMessage = (e) => {
      const d = e.data;
      const video = videoRef.current;
      if (d.type === "ready") {
        cvReadyRef.current = true;
        setCvReady(true);
        showHint("Placez la fiche sur une surface contrastée");
        return;
      }
      if (d.type === "error") {
        cvReadyRef.current = false;
        setCvReady(false);
        setAuto(false);
        showHint("Détection auto indisponible — cadrez la fiche et appuyez sur le bouton");
        return;
      }
      if (d.type === "quad") {
        busyRef.current = false;
        if (capturedRef.current || !video) return;
        const quad = d.pts ? { pts: d.pts, W: lastWHRef.current.W, H: lastWHRef.current.H } : null;
        drawOverlay(video, quad);
        if (quad) {
          if (similar(quad, lastQuad.current)) stableCount.current += 1;
          else stableCount.current = 0;
          lastQuad.current = quad;
          showHint(stableCount.current > 1 ? "Ne bougez plus…" : "Cadrez la fiche");
          if (autoRef.current && stableCount.current >= 4) {
            captureWarp(quad);
          }
        } else {
          stableCount.current = 0;
          lastQuad.current = null;
          showHint(autoRef.current ? "Placez la fiche sur une surface contrastée" : "Cadrez la fiche et appuyez");
        }
        return;
      }
      if (d.type === "captured") {
        if (d.fallback || !d.data) { captureFullFrame(); return; }
        const canvas = document.createElement("canvas");
        canvas.width = d.width; canvas.height = d.height;
        canvas.getContext("2d").putImageData(new ImageData(new Uint8ClampedArray(d.data), d.width, d.height), 0, 0);
        canvas.toBlob((b) => b && onCapture(b), "image/jpeg", 0.92);
        stop();
      }
    };

    (async () => {
      // 1) Caméra EN PREMIER (prompt immédiat, préserve le geste utilisateur iOS)
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 } },
          audio: false,
        });
        const video = videoRef.current;
        video.srcObject = stream;
        await video.play();
        setStatus("ready");
        showHint("Cadrez la fiche et appuyez sur le bouton");
      } catch (e) {
        setStatus("error");
        setHint("Accès caméra refusé. Autorisez la caméra dans les réglages, ou utilisez « Prendre une photo ».");
        return;
      }
      // 2) OpenCV DANS UN WORKER (le thread principal reste fluide)
      try {
        const w = new Worker(`${process.env.PUBLIC_URL || ""}/opencv-worker.js`);
        w.onmessage = onWorkerMessage;
        w.onerror = () => {
          cvReadyRef.current = false; setCvReady(false); setAuto(false);
          showHint("Détection auto indisponible — cadrez la fiche et appuyez sur le bouton");
        };
        workerRef.current = w;
        rafRef.current = requestAnimationFrame(tick);
      } catch (e) {
        setAuto(false); setCvReady(false);
        showHint("Détection auto indisponible — cadrez la fiche et appuyez sur le bouton");
      }
    })();

    return stop;
    // eslint-disable-next-line
  }, []);

  const manualCapture = () => {
    const video = videoRef.current;
    if (!video || capturedRef.current) return;
    if (cvReadyRef.current && lastQuad.current && workerRef.current) {
      // recadre + redresse via le worker
      const w = workerRef.current;
      const vw = video.videoWidth, vh = video.videoHeight;
      const c = document.createElement("canvas");
      c.width = vw; c.height = vh;
      c.getContext("2d", { willReadFrequently: true }).drawImage(video, 0, 0, vw, vh);
      const img = c.getContext("2d").getImageData(0, 0, vw, vh);
      capturedRef.current = true;
      setStatus("captured");
      w.postMessage({ cmd: "warp", imageData: img, vw, vh, quad: lastQuad.current }, [img.data.buffer]);
    } else {
      // plein cadre (sans détection)
      const vw = video.videoWidth, vh = video.videoHeight;
      const out = document.createElement("canvas"); out.width = vw; out.height = vh;
      out.getContext("2d").drawImage(video, 0, 0, vw, vh);
      capturedRef.current = true; setStatus("captured");
      out.toBlob((b) => b && onCapture(b), "image/jpeg", 0.92);
      if (workerRef.current) { workerRef.current.terminate(); workerRef.current = null; }
    }
  };

  return (
    <div className="fixed left-0 right-0 top-0 z-50 flex flex-col bg-black" style={{ height: "100dvh" }} data-testid="doc-scanner">
      <div
        className="absolute inset-x-0 top-0 z-20 flex items-center justify-between px-4"
        style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top))", paddingBottom: "0.75rem" }}
      >
        <button data-testid="scanner-close-btn" onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur at-press"><X size={20} /></button>
        {cvReady && (
          <button data-testid="scanner-auto-toggle" onClick={() => setAuto((a) => !a)}
            className="flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-semibold text-white backdrop-blur at-press" style={{ background: auto ? "rgba(224,90,71,0.9)" : "rgba(255,255,255,0.15)" }}>
            <ScanLine size={16} /> Auto {auto ? "ON" : "OFF"}
          </button>
        )}
      </div>

      <div className="relative flex flex-1 items-center justify-center overflow-hidden">
        <video ref={videoRef} playsInline muted className="max-h-full max-w-full" style={{ pointerEvents: "none" }} />
        <canvas ref={overlayRef} className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" style={{ maxHeight: "100%", maxWidth: "100%" }} />
        <canvas ref={procRef} className="hidden" />

        {status === "loading" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white">
            <Loader2 size={32} className="animate-spin" />
            <p className="text-sm">Démarrage de la caméra…</p>
          </div>
        )}
        {status === "captured" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60 text-white">
            <div className="flex h-16 w-16 items-center justify-center rounded-full at-pop" style={{ background: "var(--at-sage)" }}><Check size={32} /></div>
            <p className="font-semibold">Fiche capturée !</p>
          </div>
        )}
      </div>

      <div
        className="absolute inset-x-0 bottom-0 z-20 flex flex-col items-center gap-3 px-6 pt-6"
        style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}
      >
        {status === "error" ? (
          <p className="rounded-2xl bg-white/15 px-4 py-2 text-center text-sm text-white backdrop-blur" data-testid="scanner-hint">{hint}</p>
        ) : (
          <p className="rounded-full bg-white/15 px-4 py-1.5 text-sm text-white backdrop-blur" data-testid="scanner-hint">{hint}</p>
        )}
        <button
          data-testid={status === "error" ? "scanner-close-fallback" : "scanner-manual-capture"}
          onClick={status === "error" ? onClose : manualCapture}
          className="flex h-16 w-16 items-center justify-center rounded-full border-4 border-white bg-white/20 at-press"
        >
          {status === "error"
            ? <X size={24} className="text-white" />
            : <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white"><Camera size={24} style={{ color: "var(--at-brand)" }} /></span>}
        </button>
      </div>
    </div>
  );
}
